import { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Crown, Check, Sparkles } from 'lucide-react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

interface ProUpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpgradeMonthly?: () => void;
  onUpgradeYearly?: () => void;
}

export default function ProUpgradeModal({
  isOpen,
  onClose,
  onUpgradeMonthly,
  onUpgradeYearly,
}: ProUpgradeModalProps) {
  const [isLoading, setIsLoading] = useState<'monthly' | 'yearly' | null>(null);

  if (!isOpen) return null;

  const freeFeatures = [
    '1 partner (2 people max)',
    '3 custom categories',
    'Variable & fixed expenses',
    'Split defaults (KEEP / SPLIT)',
    'Basic balance tracking',
    'Web push notifications',
  ];

  const proFeatures = [
    'Up to 5 people per space',
    'Unlimited custom categories',
    'AI-powered insights',
    'Custom amounts per person',
    'Budget caps & alerts',
    'Weekly/monthly email digests',
    'CSV & PDF exports',
    'Spending trend charts',
  ];

  const handleUpgrade = async (plan: 'monthly' | 'yearly') => {
    setIsLoading(plan);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch('/api/stripe/create-checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ plan }),
      });

      const data = await res.json();

      if (data.url) {
        if (data.mock) {
          toast.success('Mock mode — Pro activated!');
          onClose();
        } else {
          window.location.href = data.url;
        }
      } else {
        toast.error(data.error || 'Failed to create checkout');
      }
    } catch {
      toast.error('Something went wrong');
    } finally {
      setIsLoading(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        onClick={onClose}
        className="absolute inset-0 bg-black/60"
      />
      <motion.div
        initial={{ y: '100%', opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="relative bg-card rounded-t-3xl sm:rounded-3xl w-full sm:max-w-md max-h-[90vh] overflow-y-auto"
      >
        <div className="sticky top-0 bg-card px-6 pt-6 pb-4 border-b border-border z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Crown className="text-yellow-400" size={24} />
              <h2 className="text-xl font-bold">Upgrade to Pro</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-muted/10 touch-target"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Feature Comparison */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <h3 className="font-semibold text-muted text-sm uppercase">Free</h3>
              {freeFeatures.map(f => (
                <div key={f} className="flex items-start gap-2 text-sm">
                  <Check className="w-3.5 h-3.5 text-primary mt-0.5 flex-shrink-0" />
                  <span>{f}</span>
                </div>
              ))}
            </div>
            <div className="space-y-2">
              <h3 className="font-semibold text-yellow-400 text-sm uppercase flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> Pro
              </h3>
              {proFeatures.map(f => (
                <div key={f} className="flex items-start gap-2 text-sm">
                  <span className="text-yellow-400 mt-0.5 flex-shrink-0">★</span>
                  <span>{f}</span>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="space-y-3">
            <button
              onClick={() => {
                if (onUpgradeMonthly) onUpgradeMonthly();
                handleUpgrade('monthly');
              }}
              disabled={!!isLoading}
              className="w-full py-3 rounded-xl bg-primary text-background font-semibold text-base hover:bg-primary/90 transition-colors touch-target disabled:opacity-50"
            >
              {isLoading === 'monthly' ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-background/30 border-t-background rounded-full animate-spin" />
                  Processing...
                </span>
              ) : (
                '$4.99 / month'
              )}
            </button>
            <button
              onClick={() => {
                if (onUpgradeYearly) onUpgradeYearly();
                handleUpgrade('yearly');
              }}
              disabled={!!isLoading}
              className="w-full py-3 rounded-xl bg-gradient-to-r from-yellow-400 to-yellow-500 text-background font-semibold text-base hover:opacity-90 transition-opacity touch-target disabled:opacity-50"
            >
              {isLoading === 'yearly' ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-background/30 border-t-background rounded-full animate-spin" />
                  Processing...
                </span>
              ) : (
                '$39.99 / year (save 32%)'
              )}
            </button>
          </div>

          <p className="text-xs text-muted text-center">
            7-day free trial included. Cancel anytime.
          </p>
        </div>
      </motion.div>
    </div>
  );
}
